<template>
  <v-container fluid>
    <v-card>
      <v-toolbar flat dense color="primary" dark>
        <v-toolbar-title>New Quotation</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <v-form>

          <!-- BASIC INFO -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field v-if="!form.projectCode" clearable hide-details="auto" label="Subject (new project code)"
                v-model="form.data.subject" outlined dense :error-messages="form.errors?.['data.subject']" />
              <v-text-field v-else hide-details="auto" label="Subject" :value="form.projectCode.description" outlined
                dense readonly />
            </v-col>

            <v-col cols="12" md="6" v-if="!form.data.subject">
              <v-autocomplete v-if="!form.data.new_customer.name" hide-details="auto" item-value="id" item-text="code"
                label="Project Code" :items="projectCodes" outlined dense clearable no-filter
                :search-input.sync="projectCodeSearch" return-object v-model="form.projectCode"
                :error-messages="form.errors?.['data.project_code_id']">
                <template v-slot:selection="{ item }">{{ item.code }}</template>
                <template v-slot:item="{ item }">
                  (<small>{{ item.code }}</small>) <small>&nbsp;-&nbsp;{{ item.description }}</small>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col cols="12" md="2" class="d-flex" style="align-items: center;">
              <!-- TOGGLE BUTTON -->
              <v-btn small text color="primary" class="mb-2 w-full" @click="newCustomer = !newCustomer">
                <v-icon left>
                  {{ newCustomer ? 'mdi-account-search' : 'mdi-account-plus' }}
                </v-icon>
                {{ newCustomer ? 'Select Existing Customer' : 'New Customer' }}
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">

              <!-- NEW CUSTOMER -->
              <div v-if="newCustomer" class="d-flex" style="gap: 10px;">
                <v-select outlined dense label="Category" hide-details="auto" item-value="id"
                  :item-text="(item) => `(${item.id}) - ${item.name}`"
                  v-model="form.data.new_customer.customer_category_id" :items="customerCategories" />

                <v-text-field outlined dense label="Company" hide-details="auto"
                  v-model="form.data.new_customer.company" />
              </div>

              <!-- EXISTING CUSTOMER -->
              <v-autocomplete v-else hide-details="auto" item-value="id" item-text="name" label="Customer"
                :items="customers" :search-input.sync="customerSearch"
                :error-messages="form.errors?.['data.customer_id']" outlined dense clearable
                v-model="form.data.customer_id">
                <template #item="{ item }">
                  <small v-html="`(${highlightText(item.id, customerSearch)})`"></small>
                  &nbsp;-&nbsp;
                  <span v-html="highlightText(item.name, customerSearch)"></span>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-select hide-details="auto" label="VAT Type" :items="vatTypes" v-model="form.data.vat_type" outlined
                dense :error-messages="form.errors?.['data.vat_type']" />
            </v-col>

            <!-- <v-col cols="12" :md="form.data.project_code ? 8 : 4" v-if="!form.projectCode">
              <v-text-field hide-details="auto" placeholder="Enter Project if new" label="New Project Code Description"
                v-model="form.data.project_code" outlined dense clearable @input="v => form.data.subject = v" />
            </v-col> -->


            <v-col cols="12" md="4">
              <v-text-field hide-details="auto" label="Actual Date" type="date" v-model="form.data.actual_date" outlined
                dense :error-messages="form.errors?.['data.actual_date']" />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field hide-details="auto" label="Discount Rate" type="number" v-model="form.data.discount_rate"
                outlined dense :error-messages="form.errors?.['data.discount_rate']" />
            </v-col>

            <v-col cols="12" md="12">
              <v-textarea hide-details="auto" label="Notes" v-model="form.data.notes" outlined dense
                :error-messages="form.errors?.['data.notes']" />
            </v-col>
          </v-row>

          <!-- TERMS & CONDITIONS -->
          <v-divider class="my-4" />

          <v-row dense>
            <v-col cols="12" md="6">
              <v-combobox hide-details="auto" v-model="selectedTac" :items="tacOptions" :loading="tacLoading"
                item-text="alias" item-value="id" label="Search / Select T&C Preset" outlined dense clearable
                return-object @change="applyTac" :error-messages="form.errors?.['data.terms_and_conditions']" />
            </v-col>

            <v-col cols="12" md="6" class="d-flex align-center">
              <v-btn color="primary" small class="mr-2" @click="saveTac">
                <v-icon left>mdi-content-save</v-icon>
                Save as Preset
              </v-btn>

              <v-btn color="warning" small :disabled="!selectedTac || !selectedTac.id" @click="updateTac">
                <v-icon left>mdi-pencil</v-icon>
                Update Preset
              </v-btn>
            </v-col>
          </v-row>

          <strong>Terms & Conditions</strong>

          <v-row dense>
            <v-col cols="12" v-for="(tc, i) in form.data.terms_and_conditions" :key="i">
              <v-card outlined class="pa-3">
                <v-row dense align="center">
                  <v-col cols="12" md="5">
                    <v-text-field hide-details="auto" label="Key" v-model="tc.key" outlined dense />
                  </v-col>

                  <v-col cols="12" md="5">
                    <v-text-field :error-messages="form.errors?.[`data.terms_and_conditions.${tc.key}`]"
                      hide-details="auto" label="Value" v-model="tc.value" outlined dense />
                  </v-col>

                  <v-col cols="12" md="2" class="text-right">
                    <v-btn color="red" text dark style="width: 100%;" @click="removeTC(i)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <v-btn small class="mt-2" @click="addTC">
            <v-icon left>mdi-plus</v-icon>
            Add Term
          </v-btn>

          <!-- ITEMS -->
          <v-divider class="my-4" />

          <strong>Items</strong>

          <v-data-table hide-default-footer :headers="itemHeaders" :items="form.items" dense class="mt-2">
            <template v-slot:item.specs="{ item }">
              <span>{{ item.specs.join(' | ') }}</span>
            </template>
            <template #item.is_service="{ value }">
              <v-icon small :color="value ? 'green' : 'red'">
                {{ value ? 'mdi-check' : '' }}
              </v-icon>
            </template>
            <template v-slot:item.actions="{ index }">
              <v-btn icon small color="red" @click="removeItem(index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:item.unit_price="{ value }">{{ currencyFormat(value) }}</template>
            <template v-slot:item.amount="{ item }">{{ currencyFormat(item.unit_price * item.quantity) }}</template>
            <template #body.append>
              <tr>
                <th colspan="5" class="text-right">Sub Total</th>
                <td class="text-right">
                  {{ currencyFormat(actualSubtotal) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">Discount ({{ discountPercent }})</th>
                <td class="text-right">
                  {{ currencyFormat(discount) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">Total (vat ex)</th>
                <td class="text-right">
                  {{ currencyFormat(totalVatEx) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">VAT ({{ getVatSymbol(form.data.vat_type) }})</th>
                <td class="text-right">
                  {{ currencyFormat(totalVatAmount) }}
                </td>
                <td>&nbsp;</td>
              </tr>

              <tr>
                <th colspan="5" class="text-right">
                  Total
                </th>
                <th class="text-right">
                  <strong class="text-h6">
                    {{ currencyFormat(totalAmount) }}
                  </strong>
                </th>
                <td>&nbsp;</td>
              </tr>
            </template>
          </v-data-table>

          <v-btn small class="mt-2" @click="itemDialog = true">
            <v-icon left>mdi-plus</v-icon>
            Add Item
          </v-btn>

          <!-- ACTION -->
          <v-divider class="my-4" />

          <v-btn color="primary" class="mt-2" @click="submit">
            Save
          </v-btn>

        </v-form>
      </v-card-text>
    </v-card>
    <v-dialog v-model="itemDialog" max-width="600px">
      <v-card>
        <v-card-title>Add Item</v-card-title>

        <v-card-text class="d-flex flex-column" style="gap: 10px;">
          <v-combobox no-filter clearable v-model="newItem.description" hide-details="auto" label="Description"
            :items="quotationItems.data.map(d => d.description)" outlined dense
            @update:search-input="fetchQuotationItems()" :search-input.sync="quotationItems.search"
            @change="onQuotationItemChange">
            <template v-slot:item="{ item }">
              <div class="d-flex flex-column">
                <h2 style="font-size: 14px;">
                  {{ item }}
                </h2>

                <p style="text-indent: 10px; font-size: 10px;">{{ quotationItemsMap.get(item).specs.join(' | ') }}</p>
              </div>
            </template>
          </v-combobox>

          <v-text-field hide-details="auto" label="Unit Price" type="number" v-model="newItem.unit_price" outlined
            dense />

          <v-text-field hide-details="auto" label="Quantity" type="number" v-model="newItem.quantity" outlined dense />

          <v-textarea hide-details="auto" label="Specs" v-model="newItem.specsText" outlined dense />

          <v-checkbox hide-details="auto" label="Is Service" v-model="newItem.is_service" dense />
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn text @click="itemDialog = false">Cancel</v-btn>

          <v-btn color="primary" @click="addItemConfirm">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
// @ts-nocheck
import appAxios from '@/libs/app-axios'
import { currencyFormat, ErrorPopup, highlightText, LoadingPopup, SuccessPopup, } from '@/libs/utils'
import { isAxiosError } from 'axios'
import _ from 'lodash'
import {
  computeQuotationDiscount,
  computeQuotationSubtotal,
  computeQuotationTotalAmount,
  computeQuotationTotalVatAmount,
  computeQuotationTotalVatEx,
  getVatSymbol
} from '@/libs/quotation-computations'

export default {
  data() {
    return {
      newCustomer: false, // 👈 add this
      customerCategories: [],
      vatTypes: [
        'VAT_EXCLUSIVE',
        // 'VAT_INCLUSIVE',
        'NON_VAT',
      ],

      form: {
        customer: null,
        projectCode: null,
        data: {
          subject: '',
          customer_id: '',
          new_customer: {
            company: '',
            customer_category_id: null,
          },
          vat_type: 'VAT_EXCLUSIVE',
          notes: '- Price may change after 30 days',
          project_code_id: null,
          project_code: '',
          discount_rate: 0,
          actual_date: '',
          terms_and_conditions: [
            { key: '', value: '' },
          ],
        },
        errors: {},
        items: [],
      },
      errors: {},
      customers: [],
      customerSearch: null,
      itemDialog: false,
      itemHeaders: [
        { text: 'Description', value: 'description' },
        { text: 'Specs', value: 'specs' },
        { text: 'Service', value: 'is_service' },
        { text: 'Unit Price', value: 'unit_price', align: 'right' },
        { text: 'Quantity', value: 'quantity', align: 'center' },
        { text: 'Amount', value: 'amount', align: 'right', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      newItem: {
        description: '',
        unit_price: 0,
        quantity: 1,
        specs: [],
        specsText: '',
        is_service: false,
      },
      quotationItems: {
        data: [],
        search: '',
      },
      debounceProjectCodeSearch: null,
      projectCodes: [],
      projectCodeSearch: null,
      tacOptions: [],
      tacLoading: false,
      selectedTac: null,
    }
  },
  computed: {
    quotationItemsMap() {
      return new Map(this.quotationItems.data.map(d => [d.description, d]))
    },

    subtotal() {
      return this.form.items.reduce((prev, item) => prev + (item.unit_price * item.quantity), 0)
    },

    actualSubtotal() {
      if (!this.subtotal) return 0
      return computeQuotationSubtotal(this.form.data.vat_type, this.subtotal)
    },

    discount() {
      if (!this.subtotal) return 0
      return computeQuotationDiscount(
        this.form.data.vat_type,
        this.subtotal,
        this.form.data.discount_rate
      )
    },

    totalVatEx() {
      if (!this.subtotal) return 0
      return computeQuotationTotalVatEx(
        this.form.data.vat_type,
        this.subtotal,
        this.form.data.discount_rate
      )
    },

    totalVatAmount() {
      if (!this.subtotal) return 0
      return computeQuotationTotalVatAmount(
        this.form.data.vat_type,
        this.subtotal,
        this.form.data.discount_rate
      )
    },

    totalAmount() {
      if (!this.subtotal) return 0
      return computeQuotationTotalAmount(
        this.form.data.vat_type,
        this.subtotal,
        this.form.data.discount_rate
      )
    },

    discountPercent() {
      if (!this.form.data.discount_rate) return '-'
      return (this.form.data.discount_rate * 100) + '%'
    }
  },
  created() {
    this.init()
    this.fetchCustomerCategories()
    this.fetchCustomers()
    this.fetchTacs()
    this.debounceProjectCodeSearch()
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') {
      this.autoFill()
    }
  },
  watch: {
    projectCodeSearch() {
      this.debounceProjectCodeSearch()
      console.log(this.projectCodeSearch)
    },
    'form.data.subject': function (val) {
      if (val) {
        this.form.data.project_code_id = null
        this.form.projectCode = null
      }
    },
    'form.projectCode': function (val) {
      this.form.data.project_code_id = val.id
      this.form.data.subject = null
    },
  },
  methods: {
    init() {
      this.debounceProjectCodeSearch = _.debounce(this.fetchProjectCodes, 500)
    },
    addTC() {
      this.form.data.terms_and_conditions.push({
        key: '',
        value: '',
      })
    },
    async fetchTacs() {
      this.tacLoading = true
      try {
        const res = await appAxios().get('/v3/quotation-tacs')
        this.tacOptions = res.data
      } finally {
        this.tacLoading = false
      }
    },
    async fetchProjectCodes() {
      try {
        const search = this.projectCodeSearch
        const res = await appAxios().get('/v3/project-codes', {
          params: {
            page: 1,
            limit: 1000,
            search,
          }
        })
        this.projectCodes = [...res.data.data]
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    autoFill() {
      this.form.data = {
        ...this.form.data,
        subject: 'Test Quotation',
        customer_id: 'BD0001',
        vat_type: 'VAT_EXCLUSIVE',
        project_code_id: null,
        project_code: 'PRJ-001',
        discount_rate: 0.05,
        actual_date: new Date().toISOString().substr(0, 10),
        terms_and_conditions: [
          { key: 'Payment Terms', value: 'Net 30 days' },
          { key: 'Delivery Terms', value: '7 days after confirmation' },
        ],
      }

      this.form.items = [
        {
          description: 'Sample Product A',
          unit_price: 150,
          quantity: 2,
          specs: ['spec A', 'spec B'],
          is_service: false,
        },
        {
          description: 'Installation Service',
          unit_price: 300,
          quantity: 1,
          specs: [],
          is_service: true,
        },
      ]
    },
    async fetchCustomers() {
      try {
        const res = await appAxios().get('/v3/customers', {
          params: {
            page: 1,
            limit: 1000,
          },
        })
        this.customers = res.data.data
      } catch (e) {
        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data?.message || 'Failed to fetch customers')
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    addSpec(itemIndex) {
      this.form.items[itemIndex].specs.push('')
    },
    removeSpec(itemIndex, specIndex) {
      this.form.items[itemIndex].specs.splice(specIndex, 1)
    },
    removeTC(i) {
      this.form.data.terms_and_conditions.splice(i, 1)
    },
    addItem() {
      this.form.items.push({
        description: '',
        unit_price: 0,
        quantity: 1,
        specs: [],
        is_service: false,
      })
    },

    removeItem(i) {
      this.form.items.splice(i, 1)
    },

    async submit() {
      try {
        LoadingPopup.fire('Loading', 'Saving Quotation!')
        const tncObject = {}
        this.form.errors = {}
        this.form.data.terms_and_conditions.forEach(tc => {
          if (tc.key) {
            tncObject[tc.key] = tc.value
          }
        })
        const data = Object.keys(this.form.data)
          .reduce((prev, next) => {
            if (next === 'project_code_id' && this.newCustomer) return prev
            if (next === 'new_customer' && !this.newCustomer) return prev
            if (next === 'customer_id' && this.newCustomer) return prev
            if (next === 'terms_and_conditions') {
              prev[next] = tncObject
              return prev
            }
            prev[next] = this.form.data[next]
            return prev
          }, {})
        const payload = {
          data,
          items: this.form.items.map(i => ({
            description: i.description,
            unit_price: i.unit_price,
            quantity: i.quantity,
            specs: i.specs,
            is_service: i.is_service,
          })),
        }

        const { data: resData } = await appAxios().post('/v3/quotations', payload)
        SuccessPopup.fire('Success', 'Successfully Quotation Saved Successfully')
        this.$router.push({ name: 'Quotations.Edit', params: { quotationId: resData.data.id } })
      } catch (e) {
        LoadingPopup.close()
        if (isAxiosError(e) && e.response?.status === 422) {
          ErrorPopup.fire('Error', e.response?.data?.message)
          const { errors = {} } = e.response?.data
          const formErrors = {}
          for (const key in errors) formErrors[key] = errors[key]
          this.form.errors = formErrors
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    addItemConfirm() {
      this.form.items.push({
        description: this.newItem.description,
        unit_price: this.newItem.unit_price,
        quantity: this.newItem.quantity,
        specs: this.newItem.specsText
          ? this.newItem.specsText.split('\n').map(s => s.trim())
          : [],
        is_service: this.newItem.is_service,
      })

      // reset dialog
      this.newItem = {
        description: '',
        unit_price: 0,
        quantity: 1,
        specs: [],
        specsText: '',
        is_service: false,
      }

      this.itemDialog = false
    },
    applyTac(tac) {
      if (!tac || !tac.properties) return

      this.form.data.terms_and_conditions = Object.entries(tac.properties).map(
        ([key, value]) => ({
          key,
          value,
        })
      )
    },
    fetchQuotationItems: _.debounce(async function () {
      try {
        const res = await appAxios().get('/v3/quotation-items', {
          params: {
            page: 1,
            limit: 1000,
            search: this.quotationItems.search,
            unique: true,
          },
        })
        this.quotationItems.data = res.data.data
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    }, 500),
    async saveTac() {
      try {
        const alias = prompt('Enter preset name (alias):')
        if (!alias) return

        const payload = {
          alias,
          properties: this.buildTacObject(),
        }

        const res = await appAxios().post('/v3/quotation-tacs', payload)
        SuccessPopup.fire('Success', res.data)
        await this.fetchTacs()
        this.selectedTac = this.tacOptions.find(opt => opt.alias === alias.trim())
      } catch (e) {
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    buildTacObject() {
      const obj = {}

      this.form.data.terms_and_conditions.forEach(tc => {
        if (tc.key) {
          obj[tc.key] = tc.value
        }
      })

      return obj
    },
    async updateTac() {
      try {
        if (!this.selectedTac?.id) return

        const payload = {
          alias: this.selectedTac.alias,
          properties: this.buildTacObject(),
        }

        const res = await appAxios().put(
          `/v3/quotation-tacs/${this.selectedTac.id}`,
          payload
        )
        SuccessPopup.fire('Success', res.data)
      } catch (e) {
        if (e instanceof Error) {
          ErrorPopup.fire('Error', e.message)
        }
      }
    },
    onQuotationItemChange(description) {
      const item = this.quotationItemsMap.get(description)
      if (!item) return
      this.newItem.specs = item.specs
      this.newItem.specsText = item.specs.join('\n')
      this.newItem.unit_price = item.unit_price
      this.newItem.is_service = item.is_service
    },
    async fetchCustomerCategories() {
      try {
        const { data } = await appAxios().get('/customer-categories', {
          params: {
            page: 1,
            limit: 1000,
          },
        })
        this.customerCategories = data
      } catch (e) {
        if (isAxiosError(e)) {
          ErrorPopup.fire('Error', e.response?.data?.message || 'Failed to fetch customer categories')
          return
        }
        if (e instanceof Error) ErrorPopup.fire('Error', e.message)
      }
    },
    highlightText,
    getVatSymbol,
    currencyFormat,
  },
}
</script>
